<template>
  <div class="sensitivity-analysis-container">
    <!-- 全屏加载遮罩层 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <div class="loading-text">计算中...</div>
      </div>
    </div>
    
    <div class="sensitivity-analysis-wrapper">
      <!-- 左上：输入区 -->
      <div class="grid-area input-section">
        <h3>敏感性分析参数设置</h3>
        <div class="input-content">
          <!-- 敏感指标多选框 -->
          <div class="input-subsection">
            <div class="input-group">
              <label class="group-label">敏感指标：</label>
              <div class="checkbox-group">
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    :checked="selectedFactors.includes('产品')"
                    @input="handleFactorChange('产品')"
                  />
                  <span>产品价格（产量）</span>
                </label>

                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    :checked="selectedFactors.includes('原料')"
                    @input="handleFactorChange('原料')"
                  />
                  <span>原辅材料</span>
                </label>
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    :checked="selectedFactors.includes('建投')"
                    @input="handleFactorChange('建投')"
                  />
                  <span>建设投资</span>
                </label>
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    :checked="selectedFactors.includes('成本')"
                    @input="handleFactorChange('成本')"
                  />
                  <span>经营成本</span>
                </label>
                <label class="checkbox-item">
                  <input
                    type="checkbox"
                    :checked="selectedFactors.includes('动力')"
                    @input="handleFactorChange('动力')"
                  />
                  <span>燃料动力</span>
                </label>
              </div>
            </div>
          </div>

          <!-- 上下浮动单选框 -->
          <div class="input-subsection">
            <div class="input-group">
              <label class="group-label">上下浮动：</label>
              <div class="radio-groups">
                <div class="radio-item">
                  <input
                    type="radio"
                    name="changeRateGroup"
                    v-model="changeRateGroup"
                    value="0"
                    @change="handleChangeRateChange"
                  />
                  <span>±1%、±2%、±3%</span>
                </div>
                <div class="radio-item">
                  <input
                    type="radio"
                    name="changeRateGroup"
                    v-model="changeRateGroup"
                    value="1"
                    @change="handleChangeRateChange"
                  />
                  <span>±5%、±10%、±15%</span>
                </div>
                <div class="radio-item">
                  <input
                    type="radio"
                    name="changeRateGroup"
                    v-model="changeRateGroup"
                    value="2"
                    @change="handleChangeRateChange"
                  />
                  <span>±10%、±20%、±20%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="calculate-section">
          <button class="calculate-btn" @click="calculateSensitivity" :disabled="loading">
            <span v-if="loading" class="loading-indicator">计算中...</span>
            <span v-else>计算</span>
          </button>
        </div>
      </div>

      <!-- 右上：图表区 -->
      <div class="grid-area chart-section">
        <h3>敏感性分析图</h3>
        <div class="chart-container">
          <div id="sensitivity-chart" style="width: 600px; height: 500px"></div>
        </div>
      </div>

      <!-- 左下：敏感度系数和临界点分析表 -->
      <div class="grid-area table-section table1">
        <h3>敏感度系数和临界点分析表</h3>
        <div class="table-content">
          <table class="sensitivity-table">
            <thead>
              <tr>
                <th class="seq-col">序号</th>
                <th class="factor-col">不确定因素</th>
                <th class="rate-col">变化率(%)</th>
                <th class="irr-col">内部收益率</th>
                <th class="coeff-col">敏感系数</th>
                <th class="critical-point-col">临界点(%)</th>
                <th class="critical-value-col">临界值</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in sensitivityTable" :key="index">
                <td class="seq-col">{{ row.seq }}</td>
                <!-- 不确定因素列：同类跨行居中，只显示敏感指标名 -->
                <td
                  class="factor-col"
                  :rowspan="row.isFirstRow ? row.rowSpan : 0"
                  v-if="row.isFirstRow"
                >
                  <span>{{ row.factorName }}</span>
                </td>
                <!-- 空单元格，用于跨行居中的其他行 -->
                <td class="factor-col" v-else style="display: none"></td>
                <td class="rate-col">{{ row.changeRate }}</td>
                <td class="irr-col">{{ row.irr }}</td>
                <td class="coeff-col">{{ row.sensitivityCoeff }}</td>
                <!-- 临界点(%)列：同类跨行居中 -->
                <td
                  class="critical-point-col"
                  :rowspan="row.isFirstRow ? row.rowSpan : 0"
                  v-if="row.isFirstRow"
                >
                  {{ row.criticalPoint }}
                </td>
                <!-- 空单元格，用于跨行居中的其他行 -->
                <td
                  class="critical-point-col"
                  v-else
                  style="display: none"
                ></td>
                <!-- 临界值列：同类跨行居中，分两行显示 -->
                <td
                  class="critical-value-col"
                  :rowspan="row.isFirstRow ? row.rowSpan : 0"
                  v-if="row.isFirstRow"
                >
                  <div class="critical-value-name">
                    {{ row.criticalValueName }}
                  </div>
                  <div class="critical-value-quantity">
                    数量临界值：{{ row.criticalValueQuantity }}
                  </div>
                </td>
                <!-- 空单元格，用于跨行居中的其他行 -->
                <td
                  class="critical-value-col"
                  v-else
                  style="display: none"
                ></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 右下：敏感性分析表 -->
      <div class="grid-area table-section table2">
        <h3>敏感性分析表</h3>
        <div class="table-content">
          <table class="sensitivity-analysis-table">
            <thead>
              <tr>
                <th class="factor-col">变化因素\变化率</th>
                <th v-for="rate in changeRates" :key="rate" class="rate-col">
                  {{ rate.toFixed(2) }}%
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in analysisTable" :key="index">
                <td class="factor-col">{{ row.factor }}</td>
                <td v-for="(value, i) in row.data" :key="i" class="value-col">
                  {{ value }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { sensitivityAnalysisCalculation } from "@/utils/comp.js";
import * as echarts from "echarts";

export default {
  name: "SensitivityAnalysis",
  data() {
    return {
      sensitivityChart: null,
      selectedFactors: ["建投", "成本", "原料"],
      changeRateGroup: "1", //默认组1
      baseIRR: 0,
      changeRates: 1,
      sensitivityTable: [],
      analysisTable: [],
      sensitivityResults: {},
      resizeHandler: null,
      calculationTimer: null,
      loading: false, // 计算中状态
    };
  },

  created() {
    // 从 VUEX 读取敏感分析数据
    if (
      this.projectData &&
      this.projectData.不确定性分析 &&
      this.projectData.不确定性分析.sensitivityAnalysis
    ) {
      const sensitivityData = this.projectData.不确定性分析.sensitivityAnalysis;
      this.baseIRR = sensitivityData.baseIRR || 0;
      this.sensitivityTable = sensitivityData.sensitivityTable || [];
      this.analysisTable = sensitivityData.analysisTable || [];
      this.sensitivityResults = sensitivityData.results || {};
      this.临界值数量 = sensitivityData.临界值数量 || 0;
      // 如果有选中的指标，使用存储的指标，否则使用默认指标
      if (sensitivityData.selectedFactors) {
        this.selectedFactors = sensitivityData.selectedFactors;
      }
      // 如果有变化率组，使用存储的变化率组，否则使用默认组
      if (sensitivityData.changeRateGroup) {
        this.changeRateGroup = sensitivityData.changeRateGroup;
      }
    }
    // 初始化变化率数组
    this.setChangeRatesByGroup(this.changeRateGroup);
  },

  mounted() {
    // 组件挂载后，如果有数据，渲染图表
    if (Object.keys(this.sensitivityResults).length > 0) {
      this.$nextTick(() => {
        this.drawSensitivityChart();
      });
    }
  },

  beforeDestroy() {
    // 清理图表资源
    if (this.sensitivityChart) {
      this.sensitivityChart.dispose();
      this.sensitivityChart = null;
    }
    // 清理resize监听器
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
    // 清理计算定时器
    if (this.calculationTimer) {
      clearTimeout(this.calculationTimer);
      this.calculationTimer = null;
    }
  },
  methods: {
    // 根据上下浮动选择设置变化率数组
    setChangeRatesByGroup(group) {
      switch (group) {
        case "group1":
        case "0":
          // ±1%、±2%、±3%
          this.changeRates = [-3, -2, -1, 0, 1, 2, 3];
          break;
        case "group2":
        case "1":
          // ±5%、±10%、±15%
          this.changeRates = [-15, -10, -5, 0, 5, 10, 15];
          break;
        case "group3":
        case "2":
          // ±10%、±20%、±30%
          this.changeRates = [-30, -20, -10, 0, 10, 20, 30];
          break;
        default:
          this.changeRates = [-3, -2, -1, 0, 1, 2, 3];
      }
    },

    // 处理上下浮动变化
    handleChangeRateChange(e) {
      this.changeRateGroup = e.target.value;
      // 更新变化率数组
      this.setChangeRatesByGroup(this.changeRateGroup);
      // 移除自动计算，仅在点击计算按钮时计算
    },

    // 处理敏感指标变化
    handleFactorChange(item) {
      //判断是否已存在
      if (this.selectedFactors.includes(item)) {
        //删除
        this.selectedFactors.splice(this.selectedFactors.indexOf(item), 1);
      } else {
        // 移除最多选择4个敏感指标的限制，允许选择所有指标
        this.selectedFactors.push(item);
      }
      // 移除自动计算，仅在点击计算按钮时计算
    },

    // 获取敏感性分析数据
    getSensitivityData() {
      // 调用敏感性分析计算函数，传递选中的敏感指标和变化率组
      const sensitivityData = sensitivityAnalysisCalculation(
        this.selectedFactors,
        this.changeRateGroup
      );

      if (sensitivityData) {
        this.baseIRR = sensitivityData.baseIRR || 0;
        this.sensitivityTable = sensitivityData.sensitivityTable || [];
        this.analysisTable = sensitivityData.analysisTable || [];
        this.sensitivityResults = sensitivityData.sensitivityResults || {
          factorResults: {},
        };
      }
    },

    // 计算敏感性分析
    async calculateSensitivity() {
      // 开始计算，设置loading状态
      this.loading = true;
      
      try {
        // 使用setTimeout模拟异步计算，确保等待动画有足够时间显示
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // 调用敏感性分析计算函数，传递选中的敏感指标和变化率组
        const sensitivityData = sensitivityAnalysisCalculation(
          this.selectedFactors,
          this.changeRateGroup
        );

        if (sensitivityData) {
          // 更新组件数据
          this.baseIRR = sensitivityData.baseIRR || 0;
          this.sensitivityResults = sensitivityData.results || {};
          this.临界值数量 = sensitivityData.临界值数量 || 0;

          // 根据敏感性分析结果生成表格数据
          this.generateTablesData(sensitivityData);

          // 绘制敏感性分析图
          this.drawSensitivityChart();

          // 将敏感性分析数据结构存储到VUEX中，而非实际数据
          this.$store.commit("setProjectData", {
            ...this.projectData,
            不确定性分析: {
              ...this.projectData.不确定性分析,
              sensitivityAnalysis: {
                baseIRR: this.baseIRR,
                sensitivityTable: this.sensitivityTable,
                analysisTable: this.analysisTable,
                results: this.sensitivityResults,
                临界值数量: this.临界值数量,
                selectedFactors: this.selectedFactors,
                changeRateGroup: this.changeRateGroup,
              },
            },
          });
        }
      } catch (error) {
        console.error("敏感性分析计算出错：", error);
      } finally {
        // 计算完成，设置loading状态为false
        this.loading = false;
      }
    },

    // 生成表格数据
    generateTablesData(sensitivityData) {
      // 生成敏感性分析表数据
      this.analysisTable = [];
      for (const factor in sensitivityData.results) {
        const factorData = sensitivityData.results[factor];
        // 调整指标名称，如将"原料"改为"原辅材料"
        const displayName =
          factorData.指标名称 === "原料" ? "原辅材料" : factorData.指标名称;
        const dataRow = {
          factor: displayName,
          data: factorData.收益率.map((irr) => irr.toFixed(2) + "%"),
        };
        this.analysisTable.push(dataRow);
      }

      // 生成敏感度系数和临界点分析表数据
      this.sensitivityTable = [];
      let seq = 1;
      for (const factor in sensitivityData.results) {
        const factorData = sensitivityData.results[factor];
        // 调整指标名称，如将"原料"改为"原辅材料"
        const displayName =
          factorData.指标名称 === "原料" ? "原辅材料" : factorData.指标名称;

        // 分开保存临界值名称和数量，用于两行显示
        const criticalValueName = factorData.临界值名称 || factorData.指标名称;
        const criticalValueQuantity = factorData.临界值;

        // 生成每种变化率的行
        factorData.收益率.forEach((irr, index) => {
          const changeRate = sensitivityData.fdarr[index] * 100;
          let sensitivityCoeff = (irr - this.baseIRR) / (changeRate / 100);
          
          // 处理敏感度系数为Infinity或NaN的情况，显示为"-"
          let sensitivityCoeffStr;
          if (isFinite(sensitivityCoeff)) {
            sensitivityCoeffStr = sensitivityCoeff.toFixed(2);
          } else {
            sensitivityCoeffStr = "-";
          }

          this.sensitivityTable.push({
            seq: seq++,
            factorName: displayName,
            changeRate: changeRate.toFixed(2) + "%",
            irr: irr.toFixed(2) + "%",
            sensitivityCoeff: sensitivityCoeffStr,
            // 为每个指标保存临界点和临界值，用于表格显示，临界点乘以100
            criticalPoint: (factorData.临界点 * 100).toFixed(2) + "%",
            // 分开保存临界值名称和数量，用于两行显示
            criticalValueName: criticalValueName,
            criticalValueQuantity: criticalValueQuantity,
            // 标记是否为该指标的第一行，用于跨行居中
            isFirstRow: index === 0,
            // 标记是否为该指标的中间行（显示临界点和临界值）
            isMiddleRow: index === Math.floor(factorData.收益率.length / 2),
            // 该指标的总行数
            rowSpan: factorData.收益率.length,
          });
        });
      }
    },

    // 绘制敏感性分析图
    drawSensitivityChart() {
      // 获取图表容器
      const chartDom = document.getElementById("sensitivity-chart");
      if (!chartDom) {
        console.warn("图表容器未找到");
        return;
      }

      // 清理旧图表实例
      if (this.sensitivityChart) {
        this.sensitivityChart.dispose();
      }

      // 处理不可见的图表容器
      const originalDisplay = chartDom.style.display;
      const originalWidth = chartDom.style.width;
      const originalHeight = chartDom.style.height;
      
      // 临时设置容器为可见状态，以便ECharts能够正确计算尺寸
      if (originalDisplay === "none" || !chartDom.offsetWidth || !chartDom.offsetHeight) {
        chartDom.style.display = "block";
        chartDom.style.width = "800px";
        chartDom.style.height = "400px";
        console.log("临时设置敏感性图表容器为可见状态");
      }

      // 初始化图表
      try {
        this.sensitivityChart = echarts.init(chartDom);
        
        // 将图表实例保存到全局
        if (!window.chartInstances) {
          window.chartInstances = {};
        }
        window.chartInstances.sensitivityChart = this.sensitivityChart;
        console.log("敏感性图表实例已保存到全局");
        
        // 恢复容器原来的显示状态
        chartDom.style.display = originalDisplay;
        if (originalWidth) chartDom.style.width = originalWidth;
        if (originalHeight) chartDom.style.height = originalHeight;
        console.log("恢复敏感性图表容器原来的显示状态");
      } catch (error) {
        console.error("敏感性图表初始化失败:", error);
        // 确保无论如何都恢复容器原来的显示状态
        chartDom.style.display = originalDisplay;
        if (originalWidth) chartDom.style.width = originalWidth;
        if (originalHeight) chartDom.style.height = originalHeight;
        return;
      }

      // 准备图表数据
      const series = [];
      const colors = ["#409EFF", "#F56C6C", "#67C23A", "#E6A23C"];
      const changeRates = this.changeRates;

      // 构建图表系列数据
      let colorIndex = 0;
      for (const factor in this.sensitivityResults) {
        const factorData = this.sensitivityResults[factor];
        const data = [];

        // 准备图表数据点
        for (let i = 0; i < factorData.收益率.length; i++) {
          data.push([changeRates[i], factorData.收益率[i]]);
        }

        series.push({
          name: factorData.指标名称,
          type: "line",
          data: data,
          lineStyle: {
            color: colors[colorIndex % colors.length],
            width: 1,
          },
          symbol: "none", // 移除指标线上的点
          itemStyle: {
            color: colors[colorIndex % colors.length],
          },
        });

        colorIndex++;
      }

      // 动态计算图表配置，根据指标数量调整
      const indicatorCount = series.length;
      const legendBottom = indicatorCount <= 2 ? "5%" : "8%";
      const gridBottom = indicatorCount <= 2 ? "15%" : "18%";
      const gridTop = "8%";

      // 图表配置
      const option = {
        title: {
          text: "",
          left: "center",
          textStyle: {
            fontSize: 14,
            fontWeight: "bold",
          },
        },
        tooltip: {
          trigger: "axis",
          formatter: function (params) {
            let result = `<div style="margin-bottom: 5px; font-weight: bold;">变化率: ${params[0].axisValue}%</div>`;
            params.forEach((param) => {
              const value = Number(param.value[1]) || 0;
              result += `
                <div style="display: flex; align-items: center; margin: 2px 0;">
                  <span style="display: inline-block; width: 8px; height: 8px; background: ${
                    param.color
                  }; margin-right: 5px; border-radius: 50%;"></span>
                  <span style="flex: 1;">${param.seriesName}:</span>
                  <span style="font-weight: bold; margin-left: 10px;">${value.toFixed(
                    2
                  )}%</span>
                </div>
              `;
            });
            return result;
          },
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderColor: "#dcdfe6",
          borderWidth: 1,
          padding: [8, 12],
          textStyle: {
            fontSize: 12,
          },
        },
        legend: {
          data: Object.values(this.sensitivityResults).map(
            (item) => item.指标名称
          ),
          bottom: legendBottom,
          textStyle: {
            fontSize: 11,
          },
          itemWidth: 12,
          itemHeight: 8,
          itemGap: 8,
        },
        grid: {
          left: "12%",
          right: "12%",
          top: gridTop,
          bottom: gridBottom,
          containLabel: true,
        },
        xAxis: {
          type: "category",
          name: "变化率 (%)",
          nameTextStyle: {
            fontSize: 11,
            fontWeight: "normal",
          },
          nameLocation: "center",
          nameGap: 25,
          axisLabel: {
            fontSize: 10,
            formatter: "{value}%",
          },
          axisLine: {
            lineStyle: {
              color: "#606266",
              width: 1,
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#C0C4CC",
              opacity: 0.6,
            },
          },
        },
        yAxis: {
          type: "value",
          name: "内部收益率 (%)",
          nameTextStyle: {
            fontSize: 11,
            fontWeight: "normal",
          },
          nameLocation: "center",
          nameGap: 40,
          axisLabel: {
            fontSize: 10,
            formatter: "{value}%",
          },
          axisLine: {
            lineStyle: {
              color: "#606266",
              width: 1,
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#C0C4CC",
              opacity: 0.6,
            },
          },
        },
        series: series,
      };

      // 渲染图表
      this.sensitivityChart.setOption(option);

      // 清理旧的resize监听器
      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
      }

      // 添加新的resize监听器，确保动态调整在窗口大小变化时也生效
      this.resizeHandler = () => {
        if (this.sensitivityChart) {
          this.sensitivityChart.resize();
          // 重新设置选项以确保动态计算生效
          this.sensitivityChart.setOption(option);
        }
      };
      window.addEventListener("resize", this.resizeHandler);
    },
  },

  computed: {
    ...mapState(["projectData"]),
  },
};
</script>

<style scoped>
.sensitivity-analysis-container {
  width: 100%;
  height: auto;
  min-height: 100vh;
  overflow-y: auto; /* 页面整体可以上下滚动 */
  display: block;
  padding: 15px;
  box-sizing: border-box;
}

.sensitivity-analysis-wrapper {
  display: grid;
  grid-template-columns: 0.4fr 0.6fr;
  grid-template-rows: auto auto; /* 使用auto自动高度 */
  grid-template-areas:
    "input table2"
    "chart table1";
  gap: 10px;
  margin-bottom: 20px;
  min-height: 600px; /* 最小高度确保布局 */
  width: 100%;
  overflow: visible; /* 允许内容溢出，由容器处理滚动 */
}

.grid-area {
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px;
  box-sizing: border-box;
  overflow: hidden; /* 区域内不滚动 */
  display: flex;
  flex-direction: column;
  height: auto; /* 自动高度 */
}

.input-section {
  grid-area: input;
}

.chart-section {
  grid-area: chart;
  height: auto; /* 自动高度 */
  display: flex;
  justify-content: center !important;
  align-content: center;
}

.table-section.table1 {
  grid-area: table1;
}

.table-section.table2 {
  grid-area: table2;
}

/* 输入区样式 */
.input-section h3 {
  margin: 0 0 6px 0;
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  flex-shrink: 0;
}

.input-content {
  flex: 1; /* 占据剩余空间 */
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0; /* 允许收缩 */
}

.input-subsection {
  flex: none; /* 不伸缩 */
}

.input-group {
  margin-bottom: 0;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.group-label {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  min-width: 70px;
  margin-top: 1px;
}

.checkbox-group,
.radio-groups {
  display: flex;
  gap: 15px;
  flex: 1;
  flex-wrap: wrap;
}

.checkbox-item,
.radio-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #606266;
  cursor: pointer;
}

.calculate-section {
  margin-top: 6px;
  display: flex;
  justify-content: flex-end;
  padding-top: 6px;
  border-top: 1px solid #e4e7ed;
  flex-shrink: 0; /* 防止按钮被压缩 */
}

.calculate-btn {
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.calculate-btn:hover {
  background-color: #66b1ff;
}

/* 加载中状态样式 */
.calculate-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.loading-indicator {
  display: inline-block;
  animation: loading 1s infinite linear;
}

@keyframes loading {
  0% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.3;
  }
}

/* 全屏加载遮罩层样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7); /* 降低透明度到70%，使遮罩层变浅 */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

/* 方形框容器 */
.loading-spinner {
  width: 100px;
  height: 100px;
  background-color: white;
  border: 2px solid #e4e7ed;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* 内部圆形加载动画 */
.loading-spinner::after {
  content: '';
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: #606266;
  font-weight: 600;
}

/* 图表区域样式 */
.chart-section {
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: auto;
}

.chart-section h3 {
  margin: 0 0 5px 0;
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  text-align: center;
  flex-shrink: 0;
}

.chart-container {
  flex: 1; /* 占据剩余空间 */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  min-height: 0; /* 允许容器收缩 */
  margin-top: -5px; /* 向上移动图表，减少与标题的间距 */
}

.chart-container::before {
  content: "";
  display: block;
  padding-top: 100%; /* 保持1:1正方形 */
}

#sensitivity-chart {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100%;
  margin: 0;
}

/* 表格区域样式 */
.table-section {
  display: flex;
  flex-direction: column;
  height: auto; /* 自动高度 */
  overflow: hidden; /* 区域内不滚动 */
}

.table-section h3 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  flex-shrink: 0;
}

.table-content {
  flex: 1; /* 占据剩余空间 */
  overflow: hidden; /* 区域内不滚动 */
  min-height: 0; /* 允许容器收缩 */
}

/* 敏感度系数和临界点分析表样式 */
.sensitivity-table {
  table-layout: auto;
  border-collapse: collapse;
  font-size: 12px;
  width: 100%;
  background-color: white;
  line-height: 1.2;
}

.sensitivity-table th,
.sensitivity-table td {
  text-align: center;
  border: 1px solid #dfe6ec;
  padding: 4px 3px;
  min-width: 80px;
  height: auto;
  font-size: 11px;
}

.sensitivity-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #303133;
}

.sensitivity-table td {
  color: #606266;
}

.seq-col {
  min-width: 50px !important;
}

.factor-col {
  min-width: 80px !important;
  text-align: left !important;
  padding-left: 5px !important;
}

.rate-col,
.irr-col,
.coeff-col,
.critical-point-col {
  min-width: 80px !important;
}

.critical-value-col {
  min-width: 120px !important;
  text-align: left !important;
  padding: 8px 5px !important;
  line-height: 1.5;
}

/* 临界值名称样式 */
.critical-value-name {
  margin-bottom: 4px;
  font-weight: 500;
}

/* 临界值数量样式 */
.critical-value-quantity {
  font-size: 11px;
}

/* 敏感性分析表样式 */
.sensitivity-analysis-table {
  table-layout: auto;
  border-collapse: collapse;
  font-size: 11px;
  width: 100%;
  background-color: white;
  line-height: 1.2;
}

.sensitivity-analysis-table th,
.sensitivity-analysis-table td {
  text-align: center;
  border: 1px solid #dfe6ec;
  padding: 4px 3px;
  min-width: 80px;
  height: auto;
  font-size: 11px;
}

.sensitivity-analysis-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #303133;
}

.sensitivity-analysis-table td {
  color: #606266;
}

.value-col {
  text-align: right !important;
  font-weight: 500;
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .sensitivity-analysis-wrapper {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
    grid-template-areas:
      "input"
      "table2"
      "chart"
      "table1";
    height: auto;
    min-height: auto;
  }

  .chart-section {
    height: 400px;
  }

  .chart-container::before {
    padding-top: 0;
  }

  #sensitivity-chart {
    position: relative;
    height: 350px;
  }

  .table-section {
    height: auto;
  }
}

.selzb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(45deg, #4d7cfe, #4d7cfe);
  box-shadow: 0 2px 4px rgba(77, 124, 254, 0.5);
  transition: all 0.3s ease;
  cursor: pointer;
}
</style>
